import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { useApolloClient } from '@apollo/client';

import { ModalModel, ModalContext } from '@notino/react-styleguide';

import useFeatureSwitches from '@hooks/useFeatureSwitches';
import { AddToCartModal } from '@sharedComponents/AddToCartButton/AddToCartModal';
import { ModalSubHeader } from '@sharedComponents/AddToCartButton/AddToCartModal/ModalSubHeader';
import { StyledHeader } from '@sharedComponents/AddToCartButton/AddToCartModal/styled';
import { useAddToCartButtonContext } from '@sharedComponents/AddToCartButton/context';
import { IModal } from '@sharedComponents/AddToCartButton/context/model';
import messages from '@sharedComponents/AddToCartButton/messages';
import { NotificationModal } from '@sharedComponents/AddToCartButton/NotificationModal';
import {
  getUser,
  getUserVariables,
} from '@sharedComponents/AddToCartButton/queries/__generated__/getUser';
import getUserQuery from '@sharedComponents/AddToCartButton/queries/user.graphql';
import { IBuyButtonProps } from '@sharedComponents/AddToCartButton/sharedComponents/BuyButton';
import { useSystemOverloadedModal } from '@sharedComponents/AddToCartButton/SystemOverloadedModal';
import { wait } from '@utils/wait';

import { updateHeaderCartOverview } from '../utils';
import { addToCart } from '../utils/addToCart';
import { addToCartResult } from '../utils/addToCartResult';

import { useUserProductsInfo } from './useUserProductsInfo';

type UseHandleAddToCartPropsType = Omit<
  IBuyButtonProps,
  'buttonType' | 'professionalModal' | 'isSpecialProduct' | 'Settings'
>;
export const useHandleAddToCartNew = ({
  isOnSpecialModal,
  productCode,
  productId,
  masterId,
}: UseHandleAddToCartPropsType) => {
  const {
    apollo: {
      mutations: { addToCart: addToCartMutation },
    },
    actions: {
      handleProductAddedToCart,
      handleIncreaseSubmitAttempts,
      handleFailedAddToCart,
    },
    state: {
      submitAttempts,
      showAddToCartModal,
      cartUrl,
      disableRedirect,
      product,
      onProductAdded,
      trackAddToCartAsync,
      handleGtmPush,
      count,
      services,
      closingModal,
    },
  } = useAddToCartButtonContext();
  const userProductsInfoResult = useUserProductsInfo(masterId);
  const {
    addToCartWarningCount,
    addToCartResendDelay,
    isDatabreakersUpsellingActive,
  } = useFeatureSwitches();

  const { hideModal } = ModalContext.useModalContext();
  const client = useApolloClient();
  const showSystemOverloadedModal = useSystemOverloadedModal();

  const loadComparisonData = React.useCallback(async () => {
    const {
      data: { userProductsInfo: loadedUserProductsInfo },
    } = await client.query<getUser, getUserVariables>({
      query: getUserQuery,
      variables: {
        productIds: [masterId.toString()],
      },
    });

    return loadedUserProductsInfo;
  }, [client, masterId]);

  return React.useCallback(
    async (e: React.SyntheticEvent<HTMLElement>) => {
      e.preventDefault();

      let userProductsInfo = userProductsInfoResult;

      if (!userProductsInfo) {
        userProductsInfo = await loadComparisonData();
      }

      handleIncreaseSubmitAttempts();

      if (submitAttempts > 0 && addToCartResendDelay > 0) {
        await wait(addToCartResendDelay);
      }

      const addToCartResponse = await addToCart({
        addToCartMutation,
        product,
        count,
        services,
        userProductsInfo,
      });

      if (isOnSpecialModal) {
        hideModal();
      }

      if (addToCartResponse.success) {
        onProductAdded();

        if (trackAddToCartAsync) {
          await trackAddToCartAsync();
        }

        const { response } = addToCartResponse;
        const result = addToCartResult({
          response,
          showAddToCartModal,
          disableRedirect,
          product,
          count,
        });

        updateHeaderCartOverview({
          cartId: response.productAddResponse.cart.cartId,
          cartSettings: result.cartSettings,
          cartUrl,
        });

        let modal: IModal;

        if (result.notificationMessage) {
          modal = {
            content: (
              <NotificationModal
                statusMessages={result.notificationMessage.messages}
                messagesValues={result.notificationMessage.values}
              />
            ),
            props: {
              center: false,
              header: (
                <StyledHeader>
                  <FormattedMessage
                    {...messages[result.notificationMessage.header]}
                  />
                </StyledHeader>
              ),
            },
          };
        } else if (showAddToCartModal) {
          modal = {
            content: (
              <AddToCartModal
                productCode={productCode}
                remainingFreeDelivery={0}
                closingModal={closingModal}
                isDatabreakersUpsellingActive={isDatabreakersUpsellingActive}
              />
            ),
            props: {
              center: false,
              withFocusTrap: true,
              size: ModalModel.Sizes.large,
              header: (
                <StyledHeader>
                  <FormattedMessage {...messages.addedToCart} />
                </StyledHeader>
              ),
              subHeader: (
                <ModalSubHeader
                  amount={count}
                  statusMessages={result.statusMessages}
                  productId={productId}
                />
              ),
              onClose: () => {
                closingModal();
                hideModal();
              },
            },
          };
        } else {
          modal = null;
        }

        handleProductAddedToCart({
          handleGtmPush,
          result,
          product,
          count,
          modal,
        });
      } else {
        handleFailedAddToCart();
        if (
          submitAttempts >= addToCartWarningCount &&
          addToCartWarningCount > 0
        ) {
          showSystemOverloadedModal();
        }
      }
    },
    [
      addToCartResendDelay,
      addToCartWarningCount,
      addToCartMutation,
      cartUrl,
      closingModal,
      count,
      disableRedirect,
      handleFailedAddToCart,
      handleGtmPush,
      handleIncreaseSubmitAttempts,
      handleProductAddedToCart,
      hideModal,
      isDatabreakersUpsellingActive,
      isOnSpecialModal,
      loadComparisonData,
      onProductAdded,
      productCode,
      productId,
      product,
      services,
      showAddToCartModal,
      showSystemOverloadedModal,
      submitAttempts,
      trackAddToCartAsync,
      userProductsInfoResult,
    ]
  );
};
